/**
 * @file organize-trip/page.tsx
 * @description Professional landing page for organizing MTB trips in Poland
 * Target: International visitors looking for guided bikepark experiences
 * SEO optimized with structured data and Open Graph tags
 */

'use client';

import { useTranslations, useLocale } from 'next-intl';
import { useState } from 'react';
import { Link } from '@/i18n/routing';
import { submitTripRequest } from '@/lib/api';
import Script from 'next/script';

export default function OrganizeTripPage() {
  const t = useTranslations('organizeTrip');
  const locale = useLocale();

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Start',
        item: `https://bikeparkmapa.pl${locale === 'pl' ? '' : `/${locale}`}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('hero.title'),
        item: `https://bikeparkmapa.pl${locale === 'pl' ? '' : `/${locale}`}/organize-trip`,
      },
    ],
  };

  // Service schema for guided MTB tours
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Guided Mountain Bike Tours',
    provider: {
      '@type': 'Organization',
      name: 'BikeParkMapa',
      url: 'https://bikeparkmapa.pl',
      logo: 'https://bikeparkmapa.pl/og-image.svg',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Poland',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'MTB Tour Packages',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: t('packages.weekend.title'),
            description: t('packages.weekend.feature1'),
          },
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'PLN',
            price: '1500',
            description: t('packages.priceFrom') + ' ' + t('packages.weekend.price'),
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: t('packages.week.title'),
            description: t('packages.week.feature1'),
          },
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'PLN',
            price: '4500',
            description: t('packages.priceFrom') + ' ' + t('packages.week.price'),
          },
        },
      ],
    },
    description: t('hero.subtitle'),
    audience: {
      '@type': 'Audience',
      audienceType: 'Mountain bikers, outdoor enthusiasts, adventure seekers',
    },
  };

  // TouristTrip schema
  const touristTripSchema = {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name: t('hero.title'),
    description: t('hero.subtitle'),
    touristType: ['Mountain Bikers', 'Adventure Travelers', 'Sport Tourists'],
    itinerary: {
      '@type': 'ItemList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: t('topBikeparks.skolnity.name'),
          description: t('topBikeparks.skolnity.description'),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: t('topBikeparks.czarnaGora.name'),
          description: t('topBikeparks.czarnaGora.description'),
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: t('topBikeparks.kasina.name'),
          description: t('topBikeparks.kasina.description'),
        },
      ],
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'PLN',
      price: '1500',
      description: t('packages.priceFrom') + ' 1500 PLN',
    },
  };

  // FAQ schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: t('faq.question1'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.answer1'),
        },
      },
      {
        '@type': 'Question',
        name: t('faq.question2'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.answer2'),
        },
      },
      {
        '@type': 'Question',
        name: t('faq.question3'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.answer3'),
        },
      },
      {
        '@type': 'Question',
        name: t('faq.question4'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.answer4'),
        },
      },
      {
        '@type': 'Question',
        name: t('faq.question5'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.answer5'),
        },
      },
      {
        '@type': 'Question',
        name: t('faq.question6'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.answer6'),
        },
      },
    ],
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    groupSize: '',
    dates: '',
    duration: '',
    experience: '',
    services: [] as string[],
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Submit to Strapi API
    const result = await submitTripRequest({
      ...formData,
      locale,
    });

    if (result.success) {
      setSubmitted(true);
    } else {
      setError(result.error || 'Unknown error occurred');
    }

    setLoading(false);
  };

  const toggleService = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  return (
    <>
      <Script
        id="breadcrumb-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Script
        id="service-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <Script
        id="tourist-trip-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(touristTripSchema) }}
      />
      <Script
        id="faq-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="organize-trip-page">
        {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>{t('hero.title')}</h1>
          <p className="hero-subtitle">{t('hero.subtitle')}</p>

          {/* Why Choose Organized Trip */}
          <div className="hero-benefits">
            <h3>{t('hero.description')}</h3>
            <div className="benefits-grid">
              <div className="benefit-item">{t('hero.benefit1')}</div>
              <div className="benefit-item">{t('hero.benefit2')}</div>
              <div className="benefit-item">{t('hero.benefit3')}</div>
              <div className="benefit-item">{t('hero.benefit4')}</div>
              <div className="benefit-item">{t('hero.benefit5')}</div>
              <div className="benefit-item">{t('hero.benefit6')}</div>
              <div className="benefit-item">{t('hero.benefit7')}</div>
              <div className="benefit-item">{t('hero.benefit8')}</div>
              <div className="benefit-item">{t('hero.benefit9')}</div>
              <div className="benefit-item">{t('hero.benefit10')}</div>
            </div>
          </div>

          <div className="hero-features">
            <div className="hero-feature">
              <span className="icon">🏔️</span>
              <span>{t('hero.feature1')}</span>
            </div>
            <div className="hero-feature">
              <span className="icon">🚵</span>
              <span>{t('hero.feature2')}</span>
            </div>
            <div className="hero-feature">
              <span className="icon">👨‍✈️</span>
              <span>{t('hero.feature3')}</span>
            </div>
          </div>
          <a href="#packages" className="hero-cta">{t('hero.cta')}</a>
        </div>
      </section>

      {/* Trip Packages */}
      <section className="packages" id="packages">
        <div className="container">
          <h2>{t('packages.title')}</h2>
          <p className="section-subtitle">{t('packages.subtitle')}</p>

          <div className="packages-grid">
            {/* Weekend Warrior */}
            <div className="package-card">
              <div className="package-badge">{t('packages.weekend.badge')}</div>
              <h3>{t('packages.weekend.title')}</h3>
              <div className="package-price">
                <span className="price-from">{t('packages.priceFrom')}</span>
                <span className="price-amount">{t('packages.weekend.price')}</span>
                <span className="price-per">{t('packages.perPerson')}</span>
              </div>
              <ul className="package-features">
                <li>✓ {t('packages.weekend.feature1')}</li>
                <li>✓ {t('packages.weekend.feature2')}</li>
                <li>✓ {t('packages.weekend.feature3')}</li>
                <li>✓ {t('packages.weekend.feature4')}</li>
                <li>✓ {t('packages.weekend.feature5')}</li>
              </ul>
              <a href="#contact" className="package-btn">{t('packages.bookNow')}</a>
            </div>

            {/* Week Adventure */}
            <div className="package-card featured">
              <div className="package-badge popular">{t('packages.week.badge')}</div>
              <h3>{t('packages.week.title')}</h3>
              <div className="package-price">
                <span className="price-from">{t('packages.priceFrom')}</span>
                <span className="price-amount">{t('packages.week.price')}</span>
                <span className="price-per">{t('packages.perPerson')}</span>
              </div>
              <ul className="package-features">
                <li>✓ {t('packages.week.feature1')}</li>
                <li>✓ {t('packages.week.feature2')}</li>
                <li>✓ {t('packages.week.feature3')}</li>
                <li>✓ {t('packages.week.feature4')}</li>
                <li>✓ {t('packages.week.feature5')}</li>
                <li>✓ {t('packages.week.feature6')}</li>
              </ul>
              <a href="#contact" className="package-btn">{t('packages.bookNow')}</a>
            </div>

            {/* Custom Trip */}
            <div className="package-card">
              <div className="package-badge">{t('packages.custom.badge')}</div>
              <h3>{t('packages.custom.title')}</h3>
              <div className="package-price">
                <span className="price-custom">{t('packages.custom.price')}</span>
              </div>
              <ul className="package-features">
                <li>✓ {t('packages.custom.feature1')}</li>
                <li>✓ {t('packages.custom.feature2')}</li>
                <li>✓ {t('packages.custom.feature3')}</li>
                <li>✓ {t('packages.custom.feature4')}</li>
                <li>✓ {t('packages.custom.feature5')}</li>
              </ul>
              <a href="#contact" className="package-btn">{t('packages.bookNow')}</a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="trust-signals">
        <div className="container">
          <h2>{t('trust.title')}</h2>
          <div className="trust-grid">
            <div className="trust-item">
              <div className="trust-icon">✓</div>
              <h3>{t('trust.guarantee1.title')}</h3>
              <p>{t('trust.guarantee1.text')}</p>
            </div>
            <div className="trust-item">
              <div className="trust-icon">🏅</div>
              <h3>{t('trust.guarantee2.title')}</h3>
              <p>{t('trust.guarantee2.text')}</p>
            </div>
            <div className="trust-item">
              <div className="trust-icon">🔒</div>
              <h3>{t('trust.guarantee3.title')}</h3>
              <p>{t('trust.guarantee3.text')}</p>
            </div>
            <div className="trust-item">
              <div className="trust-icon">💯</div>
              <h3>{t('trust.guarantee4.title')}</h3>
              <p>{t('trust.guarantee4.text')}</p>
            </div>
          </div>

          {/* Testimonials */}
          <div className="testimonials">
            <h3>{t('trust.testimonialsTitle')}</h3>
            <div className="testimonials-grid">
              <div className="testimonial-card">
                <p className="testimonial-text">{t('trust.testimonial1.text')}</p>
                <div className="testimonial-author">
                  <strong>{t('trust.testimonial1.author')}</strong>
                  <span>{t('trust.testimonial1.location')}</span>
                </div>
              </div>
              <div className="testimonial-card">
                <p className="testimonial-text">{t('trust.testimonial2.text')}</p>
                <div className="testimonial-author">
                  <strong>{t('trust.testimonial2.author')}</strong>
                  <span>{t('trust.testimonial2.location')}</span>
                </div>
              </div>
              <div className="testimonial-card">
                <p className="testimonial-text">{t('trust.testimonial3.text')}</p>
                <div className="testimonial-author">
                  <strong>{t('trust.testimonial3.author')}</strong>
                  <span>{t('trust.testimonial3.location')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="services">
        <div className="container">
          <h2>{t('services.title')}</h2>
          <p className="section-subtitle">{t('services.subtitle')}</p>

          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">👨‍✈️</div>
              <h3>{t('services.guide.title')}</h3>
              <p>{t('services.guide.description')}</p>
            </div>

            <div className="service-card">
              <div className="service-icon">🚵</div>
              <h3>{t('services.rental.title')}</h3>
              <p>{t('services.rental.description')}</p>
            </div>

            <div className="service-card">
              <div className="service-icon">🚐</div>
              <h3>{t('services.transport.title')}</h3>
              <p>{t('services.transport.description')}</p>
            </div>

            <div className="service-card">
              <div className="service-icon">🏨</div>
              <h3>{t('services.accommodation.title')}</h3>
              <p>{t('services.accommodation.description')}</p>
            </div>

            <div className="service-card">
              <div className="service-icon">🍽️</div>
              <h3>{t('services.meals.title')}</h3>
              <p>{t('services.meals.description')}</p>
            </div>

            <div className="service-card">
              <div className="service-icon">📸</div>
              <h3>{t('services.photos.title')}</h3>
              <p>{t('services.photos.description')}</p>
            </div>

            <div className="service-card">
              <div className="service-icon">🛡️</div>
              <h3>{t('services.insurance.title')}</h3>
              <p>{t('services.insurance.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* TOP Bikeparks Tour */}
      <section className="top-bikeparks">
        <div className="container">
          <h2>{t('topBikeparks.title')}</h2>
          <p className="section-subtitle">{t('topBikeparks.subtitle')}</p>

          <div className="bikeparks-grid">
            <div className="bikepark-card">
              <h3>{t('topBikeparks.skolnity.name')}</h3>
              <p>{t('topBikeparks.skolnity.description')}</p>
            </div>

            <div className="bikepark-card">
              <h3>{t('topBikeparks.czarnaGora.name')}</h3>
              <p>{t('topBikeparks.czarnaGora.description')}</p>
            </div>

            <div className="bikepark-card">
              <h3>{t('topBikeparks.kasina.name')}</h3>
              <p>{t('topBikeparks.kasina.description')}</p>
            </div>

            <div className="bikepark-card">
              <h3>{t('topBikeparks.wisla.name')}</h3>
              <p>{t('topBikeparks.wisla.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Poland */}
      <section className="why-poland">
        <div className="container">
          <h2>{t('whyPoland.title')}</h2>
          <p className="section-subtitle">{t('whyPoland.subtitle')}</p>
          <div className="why-grid">
            <div className="why-item">
              <span className="why-icon">🏆</span>
              <h3>{t('whyPoland.reason1.title')}</h3>
              <p>{t('whyPoland.reason1.description')}</p>
            </div>
            <div className="why-item">
              <span className="why-icon">💰</span>
              <h3>{t('whyPoland.reason2.title')}</h3>
              <p>{t('whyPoland.reason2.description')}</p>
            </div>
            <div className="why-item">
              <span className="why-icon">🚀</span>
              <h3>{t('whyPoland.reason3.title')}</h3>
              <p>{t('whyPoland.reason3.description')}</p>
            </div>
            <div className="why-item">
              <span className="why-icon">🌲</span>
              <h3>{t('whyPoland.reason4.title')}</h3>
              <p>{t('whyPoland.reason4.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="container">
          <h2>{t('faq.title')}</h2>
          <p className="section-subtitle">{t('faq.subtitle')}</p>

          <div className="faq-list">
            <div className="faq-item">
              <h3>{t('faq.question1')}</h3>
              <p>{t('faq.answer1')}</p>
            </div>

            <div className="faq-item">
              <h3>{t('faq.question2')}</h3>
              <p>{t('faq.answer2')}</p>
            </div>

            <div className="faq-item">
              <h3>{t('faq.question3')}</h3>
              <p>{t('faq.answer3')}</p>
            </div>

            <div className="faq-item">
              <h3>{t('faq.question4')}</h3>
              <p>{t('faq.answer4')}</p>
            </div>

            <div className="faq-item">
              <h3>{t('faq.question5')}</h3>
              <p>{t('faq.answer5')}</p>
            </div>

            <div className="faq-item">
              <h3>{t('faq.question6')}</h3>
              <p>{t('faq.answer6')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="contact" id="contact">
        <div className="container">
          <h2>{t('contact.title')}</h2>
          <p className="section-subtitle">{t('contact.subtitle')}</p>

          <div className="contact-cta-box">
            <h3>{t('contact.ctaTitle')}</h3>
            <p>{t('contact.ctaDescription')}</p>
            <Link href="/kontakt" className="contact-cta-button">
              {t('contact.ctaButton')}
            </Link>
          </div>

          {/* FORM TEMPORARILY DISABLED - Will be re-enabled when Strapi API is ready */}
          {/*
              <div className="form-row">
                <div className="form-group">
                  <label>{t('contact.form.name')}</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('contact.form.namePlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('contact.form.email')}</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder={t('contact.form.emailPlaceholder')}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('contact.form.phone')}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={t('contact.form.phonePlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('contact.form.country')}</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={e => setFormData({ ...formData, country: e.target.value })}
                    placeholder={t('contact.form.countryPlaceholder')}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('contact.form.groupSize')}</label>
                  <select
                    required
                    value={formData.groupSize}
                    onChange={e => setFormData({ ...formData, groupSize: e.target.value })}
                  >
                    <option value="">{t('contact.form.groupSizePlaceholder')}</option>
                    <option value="1-2">1-2</option>
                    <option value="3-5">3-5</option>
                    <option value="6-10">6-10</option>
                    <option value="10+">10+</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('contact.form.duration')}</label>
                  <select
                    required
                    value={formData.duration}
                    onChange={e => setFormData({ ...formData, duration: e.target.value })}
                  >
                    <option value="">{t('contact.form.durationPlaceholder')}</option>
                    <option value="weekend">Weekend (2-3 days)</option>
                    <option value="week">Week (5-7 days)</option>
                    <option value="custom">{t('contact.form.durationCustom')}</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>{t('contact.form.dates')}</label>
                <input
                  type="text"
                  value={formData.dates}
                  onChange={e => setFormData({ ...formData, dates: e.target.value })}
                  placeholder={t('contact.form.datesPlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('contact.form.experience')}</label>
                <select
                  required
                  value={formData.experience}
                  onChange={e => setFormData({ ...formData, experience: e.target.value })}
                >
                  <option value="">{t('contact.form.experiencePlaceholder')}</option>
                  <option value="beginner">{t('contact.form.experienceBeginner')}</option>
                  <option value="intermediate">{t('contact.form.experienceIntermediate')}</option>
                  <option value="advanced">{t('contact.form.experienceAdvanced')}</option>
                  <option value="mixed">{t('contact.form.experienceMixed')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('contact.form.services')}</label>
                <div className="services-checkboxes">
                  {['guide', 'rental', 'transport', 'accommodation', 'meals'].map(service => (
                    <label key={service} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.services.includes(service)}
                        onChange={() => toggleService(service)}
                      />
                      <span>{t(`contact.form.service${service.charAt(0).toUpperCase() + service.slice(1)}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>{t('contact.form.message')}</label>
                <textarea
                  rows={5}
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  placeholder={t('contact.form.messagePlaceholder')}
                />
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? t('contact.form.sending') : t('contact.form.submit')}
              </button>
            </form>
          */}
        </div>
      </section>

      <style jsx>{`
        .organize-trip-page {
          background: #f8f9fa;
        }

        /* Hero Section */
        .hero {
          position: relative;
          min-height: 600px;
          padding: 80px 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%),
                      url('/images/hero-bikes.jpg') center/cover;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: white;
          overflow: hidden;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
        }

        .hero-content {
          position: relative;
          z-index: 1;
          max-width: 900px;
          padding: 0 20px;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          margin: 0 0 1.5rem;
          line-height: 1.2;
          text-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .hero-subtitle {
          font-size: 1.5rem;
          margin: 0 0 2rem;
          opacity: 0.95;
          font-weight: 300;
        }

        .hero-benefits {
          background: rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 2.5rem;
          margin: 3rem auto;
          max-width: 1100px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .hero-benefits h3 {
          font-size: 1.75rem;
          margin-bottom: 2rem;
          font-weight: 600;
          text-align: center;
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.25rem;
          text-align: left;
        }

        .benefit-item {
          background: rgba(255, 255, 255, 0.18);
          padding: 1.25rem 1.5rem;
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.25);
          font-size: 0.95rem;
          line-height: 1.6;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .benefit-item:hover {
          background: rgba(255, 255, 255, 0.28);
          transform: translateY(-4px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
        }

        .hero-features {
          display: flex;
          gap: 3rem;
          justify-content: center;
          margin-bottom: 3rem;
          flex-wrap: wrap;
        }

        .hero-feature {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.1rem;
          font-weight: 500;
        }

        .hero-feature .icon {
          font-size: 2rem;
        }

        .hero-cta {
          display: inline-block;
          padding: 18px 48px;
          background: white;
          color: #667eea;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.2rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(0,0,0,0.2);
        }

        .hero-cta:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.3);
        }

        /* Sections */
        section {
          padding: 80px 0;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        section h2 {
          font-size: 2.5rem;
          text-align: center;
          margin: 0 0 1rem;
          color: #1a3a2e;
        }

        .section-subtitle {
          text-align: center;
          font-size: 1.2rem;
          color: #666;
          margin: 0 0 4rem;
        }

        /* Packages */
        .packages {
          background: white;
        }

        .packages-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2rem;
        }

        .package-card {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 16px;
          padding: 2.5rem;
          position: relative;
          transition: all 0.3s ease;
        }

        .package-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
          border-color: #667eea;
        }

        .package-card.featured {
          border-color: #667eea;
          box-shadow: 0 8px 30px rgba(102, 126, 234, 0.15);
        }

        .package-badge {
          display: inline-block;
          padding: 6px 16px;
          background: #e5e7eb;
          color: #374151;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
        }

        .package-badge.popular {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .package-card h3 {
          font-size: 1.8rem;
          margin: 0 0 1.5rem;
          color: #1a3a2e;
        }

        .package-price {
          margin-bottom: 2rem;
        }

        .price-from {
          display: block;
          font-size: 0.9rem;
          color: #666;
          margin-bottom: 0.5rem;
        }

        .price-amount {
          display: block;
          font-size: 3rem;
          font-weight: 800;
          color: #667eea;
          line-height: 1;
        }

        .price-per {
          display: block;
          font-size: 0.9rem;
          color: #666;
          margin-top: 0.5rem;
        }

        .price-custom {
          display: block;
          font-size: 1.5rem;
          font-weight: 600;
          color: #667eea;
        }

        .package-features {
          list-style: none;
          padding: 0;
          margin: 0 0 2rem;
        }

        .package-features li {
          padding: 0.75rem 0;
          border-bottom: 1px solid #f3f4f6;
          color: #374151;
        }

        .package-btn {
          display: block;
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          text-align: center;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .package-btn:hover {
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        /* Trust Signals */
        .trust-signals {
          background: white;
        }

        .trust-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          margin-bottom: 4rem;
        }

        .trust-item {
          text-align: center;
          padding: 1.5rem;
        }

        .trust-icon {
          width: 70px;
          height: 70px;
          line-height: 70px;
          margin: 0 auto 1.5rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 50%;
          font-size: 2rem;
          font-weight: bold;
        }

        .trust-item h3 {
          font-size: 1.2rem;
          margin: 0 0 0.75rem;
          color: #1a3a2e;
        }

        .trust-item p {
          color: #666;
          line-height: 1.6;
        }

        .testimonials {
          margin-top: 4rem;
        }

        .testimonials h3 {
          text-align: center;
          font-size: 2rem;
          margin: 0 0 3rem;
          color: #1a3a2e;
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .testimonial-card {
          background: #f8f9fa;
          padding: 2rem;
          border-radius: 12px;
          border-left: 4px solid #667eea;
          position: relative;
        }

        .testimonial-text {
          font-style: italic;
          color: #374151;
          line-height: 1.7;
          margin: 0 0 1.5rem;
        }

        .testimonial-text::before {
          content: '"';
          font-size: 3rem;
          color: #667eea;
          opacity: 0.3;
          position: absolute;
          top: 1rem;
          left: 1.5rem;
        }

        .testimonial-author {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .testimonial-author strong {
          color: #1a3a2e;
          font-size: 1rem;
        }

        .testimonial-author span {
          color: #666;
          font-size: 0.9rem;
        }

        /* Services */
        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .service-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }

        .service-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.15);
        }

        .service-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .service-card h3 {
          font-size: 1.4rem;
          margin: 0 0 1rem;
          color: #1a3a2e;
        }

        .service-card p {
          color: #666;
          line-height: 1.6;
        }

        /* TOP Bikeparks */
        .top-bikeparks {
          background: #f8f9fa;
        }

        .bikeparks-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .bikepark-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          border-left: 4px solid #667eea;
        }

        .bikepark-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.2);
          border-left-color: #764ba2;
        }

        .bikepark-card h3 {
          font-size: 1.5rem;
          margin: 0 0 1rem;
          color: #667eea;
        }

        .bikepark-card p {
          color: #666;
          line-height: 1.6;
          margin: 0;
        }

        /* Why Poland */
        .why-poland {
          background: white;
        }

        .why-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 3rem;
        }

        .why-item {
          text-align: center;
        }

        .why-icon {
          font-size: 4rem;
          display: block;
          margin-bottom: 1rem;
        }

        .why-item h3 {
          font-size: 1.3rem;
          margin: 0 0 0.75rem;
          color: #1a3a2e;
        }

        .why-item p {
          color: #666;
          line-height: 1.6;
        }

        /* FAQ Section */
        .faq {
          background: #f8f9fa;
        }

        .faq-list {
          max-width: 900px;
          margin: 0 auto;
        }

        .faq-item {
          background: white;
          padding: 2rem;
          margin-bottom: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          border-left: 4px solid #667eea;
        }

        .faq-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.15);
        }

        .faq-item h3 {
          font-size: 1.3rem;
          margin: 0 0 1rem;
          color: #1a3a2e;
          font-weight: 600;
        }

        .faq-item p {
          color: #666;
          line-height: 1.7;
          margin: 0;
        }

        /* Contact Form */
        .contact {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .contact h2,
        .contact .section-subtitle {
          color: white;
        }

        .contact-cta-box {
          max-width: 700px;
          margin: 0 auto;
          background: white;
          padding: 4rem 3rem;
          border-radius: 16px;
          text-align: center;
          box-shadow: 0 8px 30px rgba(0,0,0,0.2);
        }

        .contact-cta-box h3 {
          font-size: 2rem;
          color: #1a3a2e;
          margin: 0 0 1.5rem;
        }

        .contact-cta-box p {
          font-size: 1.2rem;
          color: #666;
          margin: 0 0 2.5rem;
          line-height: 1.6;
        }

        .contact-cta-button {
          display: inline-block;
          padding: 18px 48px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.2rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        }

        .contact-cta-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
        }

        .trip-form {
          max-width: 800px;
          margin: 0 auto;
          background: white;
          padding: 3rem;
          border-radius: 16px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.2);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #374151;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 12px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
        }

        .services-checkboxes {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          color: #374151;
        }

        .checkbox-label input {
          width: auto;
        }

        .submit-btn {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .submit-btn:hover:not(:disabled) {
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .success-message {
          max-width: 600px;
          margin: 0 auto;
          padding: 4rem 2rem;
          text-align: center;
          background: white;
          border-radius: 16px;
        }

        .success-icon {
          width: 80px;
          height: 80px;
          line-height: 80px;
          margin: 0 auto 2rem;
          background: #10b981;
          color: white;
          border-radius: 50%;
          font-size: 3rem;
        }

        .success-message h3 {
          font-size: 2rem;
          color: #1a3a2e;
          margin: 0 0 1rem;
        }

        .success-message p {
          color: #666;
          font-size: 1.1rem;
        }

        .error-message {
          background: #fee;
          border: 2px solid #f44;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          color: #c00;
        }

        /* Mobile */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.2rem;
          }

          .hero-features {
            flex-direction: column;
            gap: 1.5rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          section h2 {
            font-size: 2rem;
          }

          .trip-form {
            padding: 2rem 1.5rem;
          }

          .services-checkboxes {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      </div>
    </>
  );
}
